import React, { useState } from "react";
import ReactDOM from "react-dom/client";

const DialogContainer = ({ message, title, type, confirmText, cancelText, resolve }) => {
  const [open, setOpen] = useState(true);

  const handleClose = (value) => {
    setOpen(false);
    setTimeout(() => {
      resolve(value);
    }, 200); // Wait for transition
  };

  const isConfirm = type === "confirm";
  
  // Custom button text detection
  const resolvedConfirmText = confirmText || (isConfirm ? "Confirm" : "OK");
  const resolvedCancelText = cancelText || "Cancel";

  // Parse message: First line is heading, second is the subtext description
  let mainHeading = message;
  let subText = "";
  if (message.includes("\n")) {
    const parts = message.split("\n");
    mainHeading = parts[0];
    subText = parts.slice(1).join("\n");
  } else if (isConfirm) {
    if (message.toLowerCase().includes("delete")) {
      subText = "This action is permanent and cannot be undone.";
    }
  }

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.4)",
      backdropFilter: "blur(5px)",
      zIndex: 99999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      animation: "dialogFadeIn 0.2s ease-out",
      opacity: open ? 1 : 0,
      transition: "opacity 0.2s ease",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
    }}>
      <style>{`
        @keyframes dialogFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes dialogSlideUp {
          from { transform: translateY(16px) scale(0.96); }
          to { transform: translateY(0) scale(1); }
        }
      `}</style>
      <div style={{
        background: "var(--bg-2, #ffffff)",
        border: "1px solid var(--border, rgba(0,0,0,0.06))",
        borderRadius: "24px",
        padding: "32px",
        width: "90%",
        maxWidth: "480px",
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        animation: "dialogSlideUp 0.28s cubic-bezier(0.16, 1, 0.3, 1)",
        transform: open ? "scale(1)" : "scale(0.96)",
        transition: "transform 0.2s ease",
        color: "var(--text-primary, #1e293b)",
        boxSizing: "border-box"
      }}>
        {/* Header Title with warning icon */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
          {isConfirm ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L1 21h22L12 2zm0 3.99L20.53 19H3.47L12 5.99zM13 16h-2v2h2v-2zm0-6h-2v4h2v-4z" fill="#D97706" />
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" fill="var(--primary, #3b82f6)" />
            </svg>
          )}
          <span style={{ fontSize: "16px", fontWeight: "600", color: "var(--text-primary, #1e293b)" }}>
            {title || (isConfirm ? "Confirm Action" : "Notification")}
          </span>
        </div>

        {/* Main Heading Text */}
        <h2 style={{ margin: "0 0 8px 0", fontSize: "20px", fontWeight: "600", lineHeight: "1.4", color: "var(--text-primary, #0f172a)" }}>
          {mainHeading}
        </h2>

        {/* Sub text explanation */}
        {subText && (
          <p style={{ margin: "0 0 24px 0", color: "var(--text-secondary, #64748b)", fontSize: "14px", lineHeight: "1.5" }}>
            {subText}
          </p>
        )}

        {/* Buttons */}
        <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", marginTop: subText ? "0" : "24px" }}>
          {isConfirm && (
            <button
              onClick={() => handleClose(false)}
              style={{
                padding: "10px 22px",
                borderRadius: "10px",
                border: "1px solid var(--border, #cbd5e1)",
                background: "var(--bg-2, #ffffff)",
                color: "var(--text-secondary, #475569)",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "var(--bg-3, #f1f5f9)";
                e.target.style.borderColor = "var(--text-muted, #94a3b8)";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "var(--bg-2, #ffffff)";
                e.target.style.borderColor = "var(--border, #cbd5e1)";
              }}
            >
              {resolvedCancelText}
            </button>
          )}
          <button
            onClick={() => handleClose(true)}
            style={{
              padding: "10px 24px",
              borderRadius: "10px",
              border: "none",
              background: "linear-gradient(135deg, #3b82f6, #8b5cf6)", // Dynamic theme action color
              color: "#ffffff",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              boxShadow: "0 4px 14px rgba(59, 130, 246, 0.4)",
              transition: "opacity 0.15s ease",
            }}
            onMouseEnter={(e) => e.target.style.opacity = "0.9"}
            onMouseLeave={(e) => e.target.style.opacity = "1"}
          >
            {resolvedConfirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export const customAlert = (message, title = "Notification") => {
  return new Promise((resolve) => {
    const div = document.createElement("div");
    document.body.appendChild(div);
    const root = ReactDOM.createRoot(div);
    const cleanupResolve = (val) => {
      root.unmount();
      document.body.removeChild(div);
      resolve(val);
    };
    root.render(<DialogContainer message={message} title={title} type="alert" resolve={cleanupResolve} />);
  });
};

export const customConfirm = (message, title = "Confirm Action", confirmText = "Confirm", cancelText = "Cancel") => {
  return new Promise((resolve) => {
    const div = document.createElement("div");
    document.body.appendChild(div);
    const root = ReactDOM.createRoot(div);
    const cleanupResolve = (val) => {
      root.unmount();
      document.body.removeChild(div);
      resolve(val);
    };
    root.render(<DialogContainer message={message} title={title} type="confirm" confirmText={confirmText} cancelText={cancelText} resolve={cleanupResolve} />);
  });
};

const originalConfirm = window.confirm;

export const injectGlobalOverrides = () => {
  window.alert = (msg) => {
    customAlert(msg);
  };
  window.confirm = (msg) => {
    console.warn("Synchronous confirm blocked. Please use await customConfirm(msg)");
    return originalConfirm(msg);
  };
};

export default { customAlert, customConfirm, injectGlobalOverrides };
